/**
 * @fileoverview Renders a `data:image/svg+xml;base64,…` QR (as returned by the
 * backend's generateLabeledQR) on both web and native.
 *
 * RN's <Image> renders SVG data URIs on web but NOT on native — there it shows
 * nothing. So on native we decode the base64 to raw SVG markup and hand it to
 * react-native-svg's <SvgXml>. The intrinsic aspect ratio is read from the
 * SVG's viewBox so the height matches whatever the backend produced.
 */

import React from 'react';
import { Image, Platform, View } from 'react-native';
import { SvgXml } from 'react-native-svg';

interface Props {
  /** Data URI: `data:image/svg+xml;base64,…`. */
  uri: string;
  /** Rendered width in px. Height is derived from the SVG viewBox. */
  width: number;
  /** Fallback aspect ratio (height/width) if the viewBox can't be parsed. */
  fallbackRatio?: number;
  accessibilityLabel?: string;
}

export default function QrSvgView({ uri, width, fallbackRatio = 480 / 320, accessibilityLabel = 'QR code' }: Props) {
  let svgXml = '';
  let height = Math.round(width * fallbackRatio);
  try {
    const b64 = uri.replace(/^data:image\/svg\+xml;base64,/, '');
    svgXml = typeof atob === 'function' ? atob(b64) : '';
    const vb = svgXml.match(/viewBox="0\s+0\s+([\d.]+)\s+([\d.]+)"/);
    if (vb) {
      const vbW = parseFloat(vb[1]);
      const vbH = parseFloat(vb[2]);
      if (vbW > 0) height = Math.round(width * (vbH / vbW));
    }
  } catch { /* non-critical: fall back to default ratio */ }

  return (
    <View style={{ alignItems: 'center' }}>
      {Platform.OS === 'web' || !svgXml ? (
        <Image
          source={{ uri }}
          style={{ width, height }}
          resizeMode="contain"
          accessibilityLabel={accessibilityLabel}
        />
      ) : (
        <SvgXml xml={svgXml} width={width} height={height} />
      )}
    </View>
  );
}
